import {
  PaginationState,
  Row,
  SortingState,
  Table,
} from '@tanstack/react-table';
import { FC, PropsWithChildren, ReactElement } from 'react';

import { rem } from '@savgroup-front-common/primitive/helpers/units';
import { MessageType } from '@savgroup-front-common/types';

import { Skeleton } from '../../../atoms/Skeleton';
import { SafeFormattedMessageWithoutSpread } from '../../../formatters';
import { FloatingLoader } from '../../../molecules/FloatingLoader';

import Body from './Body';
import {
  $NoResultContainer,
  $StyledTable,
  $StyledTBody,
  $TableContainer,
} from './DatatableCore.styles';
import Footer from './Footer';
import Header from './Header';

interface TableProps<D> {
  table: Table<D>;
  sorting?: SortingState;
  isLoading: boolean;
  pagination: PaginationState;
  SubComponent?: FC<{ row: Row<D> }>;
  portalId?: string;
  isSubRowStyled?: boolean;
  isExpandedBackgroundEnabled?: boolean;
  onClickRow?: (row: any) => void;
  withHeader: boolean;
  withMinimalWidth: boolean;
  noResultText?: MessageType;
  dataTestId?: string;
}

const DatatableCore = <D = unknown,>({
  table,
  sorting,
  isLoading,
  pagination,
  SubComponent,
  portalId,
  isSubRowStyled,
  isExpandedBackgroundEnabled,
  onClickRow,
  withHeader,
  withMinimalWidth,
  noResultText,
  dataTestId,
}: PropsWithChildren<TableProps<D>>): ReactElement => {
  const { rows } = table.getRowModel();

  const hasFooter = table
    .getAllColumns()
    .some((column) => column.columnDef.footer != null);

  return (
    <$TableContainer data-stretch>
      {portalId && <div id={portalId} />}
      <$StyledTable
        $withMinimalWidth={withMinimalWidth}
        data-testid={dataTestId || undefined}
      >
        {withHeader && (!isLoading || rows.length > 0) && (
          <Header<D> table={table} sorting={sorting} dataTestId={dataTestId} />
        )}
        <$StyledTBody>
          {(!isLoading || rows.length > 0) && (
            <Body<D>
              table={table}
              SubComponent={SubComponent}
              isSubRowStyled={isSubRowStyled}
              isExpandedBackgroundEnabled={isExpandedBackgroundEnabled}
              onClickRow={onClickRow}
              dataTestId={dataTestId}
            />
          )}
        </$StyledTBody>
        {hasFooter && (!isLoading || rows.length > 0) && (
          <Footer<D> table={table} dataTestId={dataTestId} />
        )}
      </$StyledTable>
      {isLoading && rows.length === 0 && (
        <>
          {new Array(pagination.pageSize).fill(null).map((_x, index) => (
            <Skeleton key={index} height={rem(28)} marginBottom={rem(7)} />
          ))}
        </>
      )}
      {!isLoading && rows.length === 0 && noResultText && (
        <$NoResultContainer>
          <SafeFormattedMessageWithoutSpread message={noResultText} />
        </$NoResultContainer>
      )}
      {isLoading && rows.length > 0 && <FloatingLoader />}
    </$TableContainer>
  );
};

export default DatatableCore;
